import closeFill from '@iconify/icons-eva/close-fill';
import baselineLogout from '@iconify/icons-ic/baseline-logout';
import { Icon } from '@iconify/react';
import { Box, Drawer, FormControl, InputLabel, MenuItem, Select, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';

import { IGroup } from '@customTypes/group';
import MHidden from 'components/@material-extend/MHidden/MHidden';
import { IconButtonAnimate } from 'components/animate';
import Logo from 'components/Logo/Logo';
import MyAvatar from 'components/MyAvatar/MyAvatar';
import NavSection, { NavButton } from 'components/NavSection/NavSection';
import { logout } from 'external/firebase/controllers/auth';
import { getGroupStudentPointsBalanceSheet, getUserGroups } from 'external/firebase/firestore/collections/groups';
import useAuth from 'hooks/useAuth';
import useCollapseDrawer from 'hooks/useCollapseDrawer';
import useLocales from 'hooks/useLocales';
import useResponsive from 'hooks/useResponsive';
import { PATH_AUTH, PATH_DASHBOARD } from 'routes/paths';
import { calculateValidPointsForRewards } from 'utils/pointsUtils';

import getSidebarConfig from './config';

// ----------------------------------------------------------------------

const DRAWER_WIDTH = 280;
const COLLAPSE_WIDTH = 102;

const RootStyle = styled('div')(({ theme }) => ({
    [theme.breakpoints.up('lg')]: {
        flexShrink: 0,
        transition: theme.transitions.create('width', {
            duration: theme.transitions.duration.complex,
        }),
    },
}));

const AccountStyle = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    padding: theme.spacing(2, 2.5),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.default,
    border: `solid 2px ${theme.palette.common.black}`,
}));

// ----------------------------------------------------------------------

interface DashboardSidebarProps {
    isOpenSidebar: boolean;
    onCloseSidebar: () => void;
}

const DashboardSidebar: React.FunctionComponent<DashboardSidebarProps> = ({ isOpenSidebar, onCloseSidebar }) => {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const { translate } = useLocales();
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const { isCollapsed } = useCollapseDrawer();
    const { user, profile, selectedGroup, onChangeSelectedGroup } = useAuth();

    const [groups, setGroups] = useState<IGroup[]>([]);
    const [validPoints, setValidPoints] = useState<number>(0);
    const [currentDate] = useState(new Date());

    const userName = user?.displayName || `${profile?.firstName} ${profile?.lastName}` || '';

    const fetchUserGroups = async (userId: string) => {
        try {
            const userGroups = await getUserGroups(userId);
            if (!selectedGroup) {
                const firstActiveGroup = userGroups.find(group => group.status === 'active');
                onChangeSelectedGroup(firstActiveGroup?.id);
            }
            setGroups(userGroups);
        } catch (err) {
            console.error(err);
        }
    };

    const fetchGroupStudentPoints = async (groupId: string, userId: string) => {
        try {
            const balanceSheet = await getGroupStudentPointsBalanceSheet(groupId, userId);
            const schoolYearPoints = calculateValidPointsForRewards(balanceSheet, currentDate);
            setValidPoints(schoolYearPoints);
        } catch (err) {
            console.error(err);
            setValidPoints(0);
        }
    };

    useEffect(() => {
        if (profile && selectedGroup) {
            fetchGroupStudentPoints(selectedGroup.id, profile.id);
        }
    }, [profile, selectedGroup]);

    useEffect(() => {
        if (profile) {
            fetchUserGroups(profile.id);
        }
    }, [profile]);

    useEffect(() => {}, []);

    useEffect(() => {
        if (isOpenSidebar) {
            onCloseSidebar();
        }
    }, [pathname]);

    const handleLogout = async () => {
        try {
            await logout();
            navigate('/');
        } catch (error) {
            enqueueSnackbar(translate('unable_to_logout'), {
                variant: 'error',
                // eslint-disable-next-line react/display-name
                action: key => (
                    <IconButtonAnimate size='small' onClick={() => closeSnackbar(key)}>
                        <Icon icon={closeFill} />
                    </IconButtonAnimate>
                ),
            });
        }
    };

    const renderContent = (
        <>
            <Stack
                spacing={3}
                sx={{
                    px: 2.5,
                    pt: 3,
                    pb: 2,
                    ...(isCollapsed && {
                        alignItems: 'center',
                    }),
                }}
            >
                <Stack direction='row' alignItems='center' justifyContent='space-between'>
                    <Box component={RouterLink} to={PATH_DASHBOARD.root} sx={{ display: 'inline-flex' }}>
                        <Logo iconOnly={isCollapsed} white />
                    </Box>
                </Stack>

                {isCollapsed ? (
                    <MyAvatar sx={{ mx: 'auto', mb: 2 }} />
                ) : (
                    <AccountStyle>
                        <Box display='flex' flexDirection='row' justifyContent='start' gap={2} sx={{ width: '100%' }}>
                            <MyAvatar />
                            <Box>
                                <Typography variant='subtitle2' sx={{ color: 'text.primary' }}>
                                    {userName}
                                </Typography>

                                {profile?.role && profile?.role !== 'child' && (
                                    <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                                        {/* @ts-ignore */}
                                        {translate(profile?.role)}
                                    </Typography>
                                )}
                                {profile?.role === 'child' && (
                                    <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                                        {validPoints} {translate('points')}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                        {profile?.role === 'child' && (
                            <Stack spacing={3} sx={{ mt: 3, width: '100%' }}>
                                <FormControl>
                                    <InputLabel id='child-select-label'>{translate('group')}</InputLabel>
                                    <Select
                                        labelId='child-select-label'
                                        value={selectedGroup?.id || ''}
                                        defaultValue={selectedGroup?.id || ''}
                                        label={translate('group')}
                                        onChange={event => onChangeSelectedGroup(event.target.value)}
                                    >
                                        {groups.map(group => (
                                            <MenuItem
                                                key={group.id}
                                                value={group.id}
                                                sx={{
                                                    ...(group.status === 'active' && {
                                                        bgcolor: 'lightgreen',
                                                    }),
                                                }}
                                            >
                                                {group.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Stack>
                        )}
                    </AccountStyle>
                )}
            </Stack>

            <NavSection navConfig={getSidebarConfig(selectedGroup)} isShow={!isCollapsed} role={profile?.role} />

            <Box sx={{ flexGrow: 1 }} />
            <Stack
                spacing={3}
                sx={{
                    pb: 2,
                }}
                direction='row'
                alignItems='center'
                justifyContent='space-between'
            >
                <NavButton
                    item={{
                        title: 'logout',
                        path: PATH_AUTH.login,
                        icon: <Icon icon={baselineLogout} width='48' height='48' />,
                    }}
                    isShow={!isCollapsed}
                    handleClick={handleLogout}
                />
            </Stack>
        </>
    );

    return (
        <RootStyle
            sx={{
                width: {
                    lg: isCollapsed ? COLLAPSE_WIDTH : DRAWER_WIDTH,
                },
            }}
        >
            <MHidden width='lgUp'>
                <Drawer
                    open={isOpenSidebar}
                    onClose={onCloseSidebar}
                    PaperProps={{
                        sx: {
                            width: DRAWER_WIDTH,
                            bgcolor: 'primary.main',
                        },
                    }}
                >
                    {renderContent}
                </Drawer>
            </MHidden>

            <MHidden width='lgDown'>
                <Drawer
                    open
                    variant='persistent'
                    PaperProps={{
                        sx: {
                            width: DRAWER_WIDTH,
                            bgcolor: 'primary.main',
                            ...(isCollapsed && {
                                width: COLLAPSE_WIDTH,
                            }),
                        },
                    }}
                >
                    {renderContent}
                </Drawer>
            </MHidden>
        </RootStyle>
    );
};

export default DashboardSidebar;
